import { FlexBox } from '@dixa/lib-shared-habla';
import noop from 'lodash/noop';
import { ChangeEvent, useCallback, useState } from 'react';

import { InstagramFeatureToggle } from './components/instagramFeatureToggle/instagramFeatureToggle';

/** Represents the on change handler for a feature switch. */
type HandleFeatureChange = (event: ChangeEvent<HTMLInputElement>) => void;

export type InstagramFeatures = {
  /** Whether story mentions are enabled. */
  isStoryMentionsEnabled?: boolean;
  /** Whether story comments are enabled. */
  isStoryCommentsEnabled?: boolean;
};

export type InstagramFeaturesProps = {
  /** The features. */
  features?: InstagramFeatures;
  /** The callback for whenever the features change. */
  onChange: (features: InstagramFeatures) => void;
};

export const InstagramFeatures = (
  props: InstagramFeaturesProps,
): JSX.Element => {
  const [isStoryMentionsEnabled, setIsStoryMentionsEnabled] = useState(
    props.features?.isStoryMentionsEnabled,
  );
  const [isStoryCommentsEnabled, setIsStoryCommentsEnabled] = useState(
    props.features?.isStoryCommentsEnabled,
  );

  /** Handles toggling the mentions feature on/off. */
  const handleMentionsFeatureChange = useCallback<HandleFeatureChange>(
    (event) => {
      const { onChange } = props;

      setIsStoryMentionsEnabled(event.target.checked);
      onChange({
        isStoryMentionsEnabled: event.target.checked,
        isStoryCommentsEnabled,
      });
    },
    [isStoryCommentsEnabled, props],
  );

  /** Handles toggling the comments feature on/off. */
  const handleCommentsFeatureChange = useCallback<HandleFeatureChange>(
    (event) => {
      const { onChange } = props;

      setIsStoryCommentsEnabled(event.target.checked);
      onChange({
        isStoryMentionsEnabled,
        isStoryCommentsEnabled: event.target.checked,
      });
    },
    [isStoryMentionsEnabled, props],
  );

  return (
    <FlexBox gap="300" flexDirection="column" margin="300 0 0">
      <InstagramFeatureToggle
        input={{
          name: 'messages-enabled',
          value: 'messages',
          checked: true,
          disabled: true,
          onChange: noop,
        }}
        inputLabel="Receive direct messages"
        tooltipLabel="Conversations are created when a user is engaging directly with your Instagram page. Every new user message will appear in the Dixa conversation"
      />
      <InstagramFeatureToggle
        input={{
          name: 'mentions-enabled',
          value: 'mentions',
          checked: isStoryMentionsEnabled,
          onChange: handleMentionsFeatureChange,
        }}
        inputLabel="Receive story mentions"
        tooltipLabel="Conversations are created when a user mentions your Instagram handle inside their story. Every story mention will appear in the Dixa conversation and you will be able to engage via a direct message with the user that posted the story"
      />
      <InstagramFeatureToggle
        input={{
          name: 'comments-enabled',
          value: 'comments',
          checked: isStoryCommentsEnabled,
          onChange: handleCommentsFeatureChange,
        }}
        inputLabel="Receive story comments"
        tooltipLabel="Conversations are created when a user adds a comment to any of the stories you posted. Every story comment will appear in the Dixa conversation and you will be able to engage via a direct message with the user that posted the story"
      />
    </FlexBox>
  );
};
